import React, { useState, useEffect } from 'react';
import { Dropdown, Menu, message, Tooltip } from 'antd';
import { DownloadOutlined, FormOutlined, MoreOutlined } from '@ant-design/icons';
import styled from 'styled-components/macro';

import { useCheckDownloadTypeLazyQuery } from '../../../../../../graphql/search.generated';
import { EntityType, AndFilterInput, SearchAcrossEntitiesInput } from '../../../../../../types.generated';
import useDropdownKeyboard from '../../../../../hooks/useDropdownKeyboard';
import { useTranslation } from '../../../../../../i18n/useTranslation';
import DownloadAsCsvModal from './DownloadAsCsvModal';
import { SearchResultsInterface } from './types';
import { useExportDataProductAssetsLazyQuery } from '../../../../../../graphql/dataProduct.generated';
import { useEntityData } from '../../../EntityContext';

const SEARCH_PAGE_SIZE_FOR_DOWNLOAD = 500;

const EMAIL = 'email';
const FILE = 'file';

const MenuIcon = styled(MoreOutlined)`
    font-size: 20px;
    height: 20px;
`;

type Props = {
    filters: AndFilterInput[];
    query: string;
    totalResults?: number;
    entityFilters: EntityType[];
    callSearchOnVariables?: (variables: {
        input: SearchAcrossEntitiesInput;
    }) => Promise<SearchResultsInterface | null | undefined>;
    setShowSelectMode?: (showSelectMode: boolean) => any;
    overrideCheckDownloadType?: boolean;
    isReadOnly?: boolean;
};

// currently only contains Download As Csv but will be extended to contain other actions as well
export default function SearchExtendedMenu({
    callSearchOnVariables,
    entityFilters,
    filters,
    query,
    totalResults,
    setShowSelectMode,
    overrideCheckDownloadType,
    isReadOnly,
}: Props) {
    const { t } = useTranslation();
    const { visible, setVisible, handleKeyDown, toggleDropdown } = useDropdownKeyboard();
    const [isDownloadingCsv, setIsDownloadingCsv] = useState(false);
    const [showDownloadAsCsvModal, setShowDownloadAsCsvModal] = useState(false);

    const [checkDownloadTypeQuery, { data: downloadType }] = useCheckDownloadTypeLazyQuery();
    const [exportDataProductAssetsQuery, { data: dataproductDownloadType }] = useExportDataProductAssetsLazyQuery();
    const { urn } = useEntityData();

    const handleDownloadAsCsv = () => {
        /**
         * Refetches a GraphQL query to check the download type.
         * This function is used to verify the download type of a specific resource
         * by refetching the relevant GraphQL query with updated variables.
         */
        if (overrideCheckDownloadType) {
            const overridenFilter = [
                {
                    and: [
                        {
                            field: 'urn',
                            value: urn,
                        },
                        ...(filters[0]?.and || []),
                    ],
                },
            ];
            exportDataProductAssetsQuery({
                variables: {
                    input: {
                        windowLocation: window.location.origin,
                        types: entityFilters,
                        query,
                        start: 0,
                        count: SEARCH_PAGE_SIZE_FOR_DOWNLOAD,
                        orFilters: overridenFilter,
                    },
                },
            });
            return;
        }
        checkDownloadTypeQuery({
            variables: {
                input: {
                    windowLocation: window.location.origin,
                    types: entityFilters,
                    query,
                    start: 0,
                    count: SEARCH_PAGE_SIZE_FOR_DOWNLOAD,
                    orFilters: filters,
                },
            },
        });
    };

    const getTooltip = () => {
        if (isReadOnly) {
            return t('ASSET_MARKED_AS_READ_ONLY');
        }
        return null;
    };

    useEffect(() => {
        const { checkDownloadType }: any = downloadType || {};
        const { exportDataProductAssets }: any = dataproductDownloadType || {};
        if (checkDownloadType || exportDataProductAssets) {
            switch (checkDownloadType?.type || exportDataProductAssets?.type) {
                case EMAIL:
                    message.success(t('Started Processing, You will receive an email shortly'));
                    break;
                case FILE:
                    setShowDownloadAsCsvModal(true);
                    break;
                default:
                    message.error(t('Something went wrong'));
            }
        }

        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [downloadType, dataproductDownloadType]);

    const menu = (
        <Menu>
            <Menu.Item
                key="0"
                tabIndex={0}
                disabled={isDownloadingCsv}
                onClick={() => {
                    handleDownloadAsCsv();
                    setVisible(false);
                }}
                icon={<DownloadOutlined className="f-icon-size-xs" />}
                aria-label={isDownloadingCsv ? t('Downloading') : t('Download')}
            >
                {isDownloadingCsv ? t('Downloading') : t('Download')}
            </Menu.Item>
            {setShowSelectMode && (
                <Tooltip title={getTooltip()} placement="left" overlayClassName="f-tooltip-white">
                    <span>
                        <Menu.Item
                            key="1"
                            tabIndex={0}
                            aria-label={t('Edit')}
                            onClick={() => {
                                setShowSelectMode(true);
                                setVisible(false);
                            }}
                            icon={<FormOutlined className="f-icon-size-xs" />}
                            disabled={isReadOnly}
                        >
                            {t('Edit')}
                        </Menu.Item>
                    </span>
                </Tooltip>
            )}
        </Menu>
    );

    return (
        <>
            <DownloadAsCsvModal
                downloadType={downloadType || dataproductDownloadType}
                filters={filters}
                query={query}
                entityFilters={entityFilters}
                callSearchOnVariables={callSearchOnVariables}
                setIsDownloadingCsv={setIsDownloadingCsv}
                showDownloadAsCsvModal={showDownloadAsCsvModal}
                setShowDownloadAsCsvModal={setShowDownloadAsCsvModal}
                totalResults={totalResults}
                isDownloadingCsv={isDownloadingCsv}
            />
            <Dropdown overlay={menu} trigger={['click']} open={visible} onOpenChange={(flag) => setVisible(flag)}>
                <MenuIcon
                    tabIndex={0}
                    onClick={toggleDropdown}
                    onKeyDown={handleKeyDown}
                    aria-label={t('More options')}
                    data-testid="three-dot-menu"
                />
            </Dropdown>
        </>
    );
}
